/**
 * 인증 상태
 * ---------------------------------------------------------------------------
 * Access Token 은 메모리(api-client)에만 두고, 프로필은 여기서 관리한다.
 * 새로고침하면 토큰이 사라지므로 refresh 쿠키로 복구한다.
 */

import { createContext, useContext } from 'react';

export interface AuthProfile {
  userId: string;
  tenantId: string;
  tenantSlug: string;
  companyName: string;
  name: string;
  email: string;
  roles: string[];
  permissions: string[];
}

export interface AuthState {
  profile: AuthProfile | null;
  status: 'loading' | 'authenticated' | 'anonymous';
  setProfile: (profile: AuthProfile | null) => void;
  signOut: () => Promise<void>;
}

export const AuthContext = createContext<AuthState>({
  profile: null,
  status: 'loading',
  setProfile: () => undefined,
  signOut: async () => undefined,
});

export const useAuth = (): AuthState => useContext(AuthContext);

/**
 * 화면 표시용 권한 검사.
 *
 * 주의: 이것은 UX 목적일 뿐 보안 장치가 아니다.
 *       실제 차단은 서버의 AuthorizationGuard 가 수행한다.
 */
export function hasPermission(profile: AuthProfile | null, required: string): boolean {
  if (!profile) return false;

  // 배열이 없을 수도 있다고 보고 막는다. 응답 한 군데가 빠뜨리면 화면 전체가
  // 터지는데(실제로 /auth/me 가 그랬다), 그 대가로 버튼 하나 안 보이는 쪽이 낫다.
  return (profile.permissions ?? []).some((granted) => {
    if (granted === required) return true;

    const [gScope, gAction] = granted.split(':');
    const [rScope, rAction] = required.split(':');
    if (!gScope || !rScope) return false;

    const [gProduct, gResource] = gScope.split('.');
    const [rProduct, rResource] = rScope.split('.');

    const scopeOk = gProduct === rProduct && (gResource === '*' || gResource === rResource);
    const actionOk = gAction === '*' || gAction === rAction;
    return scopeOk && actionOk;
  });
}
